import { Core } from "@faire/design-tokens";
import { EventIcon } from "@faire/retailer-visitor-shared/components/Events/shared/EventIcon";
import { useEventsStore } from "@faire/retailer-visitor-shared/stores/domain/Events/shared/EventsStore";
import { ICurrentPromotionalEventResponse } from "@faire/web-api--source/indigofair/data/ICurrentPromotionalEventResponse";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Row } from "@faire/web/slate/Layout";
import { getEventTheme } from "@faire/web/slate/Theme/eventsThemeColors";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Tooltip } from "@faire/web/slate/Tooltip";
import { Typography } from "@faire/web/slate/Typography";
import React from "react";
import styled, { css } from "styled-components";

interface IProps {
  className?: string;
  expandable: boolean;
  showToolTip?: boolean;
}

export const EventsThemeBadge: React.FC<IProps> = ({
  expandable: isExpandable,
  className,
  showToolTip,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { nextOrActiveEventTheme, nextOrActive: nextOrActiveEvent } =
    useEventsStore(["nextOrActiveEventTheme", "nextOrActive"]);
  const eventsThemeColors = getEventTheme("faireMarket");

  const currentEventBadgeDetails = () => {
    switch (nextOrActiveEvent?.event?.event_group) {
      case ICurrentPromotionalEventResponse.IPromotionalEvent
        .PromotionalEventGroup.MARKET:
      default:
        return {
          flyoutText: strictLocalize({
            id: "GlfaKp",
            defaultMessage: "Market special",
            description: {
              text: "Flyout text for brand tiles that have a market special",
            },
          }),
          flyoutTextColor: eventsThemeColors.promoText,
          hoverBackgroundColor: eventsThemeColors.badgeBackground,
          hasBorder: true,
        };
    }
  };

  const eventIcon = nextOrActiveEventTheme.eventIcon;

  const BadgeComponent = eventIcon?.Component;

  if (!BadgeComponent) {
    return null;
  }

  const { flyoutText, flyoutTextColor, hoverBackgroundColor, hasBorder } =
    currentEventBadgeDetails();

  const backgroundColor = eventIcon?.backgroundColor;

  const innerBody = (
    <Wrapper
      $animated={isExpandable && !showToolTip}
      $hoverBackgroundColor={hoverBackgroundColor}
      data-test-id="events-theme-badge"
    >
      <InnerBorder
        $backgroundColor={backgroundColor}
        $hasBorder={hasBorder}
        className="inner-border"
      />
      <BadgeSvg customIcon={BadgeComponent} size="32px" />
      {isExpandable && !showToolTip ? (
        <OffsetContainer>
          <FlyoutTextContainer className="flyout-text-container">
            <FlyoutText color={flyoutTextColor}>{flyoutText}</FlyoutText>
          </FlyoutTextContainer>
        </OffsetContainer>
      ) : null}
    </Wrapper>
  );

  const getEventThemeBody = () => {
    if (isExpandable && showToolTip) {
      return (
        <Tooltip
          targetNode={innerBody}
          contents={[{ body: flyoutText }]}
          width={280}
          pointerOffset={-80}
        />
      );
    }
    return innerBody;
  };

  return (
    <AbsoluteWrapper className={className}>
      {getEventThemeBody()}
    </AbsoluteWrapper>
  );
};

const AbsoluteWrapper = styled.div`
  position: absolute;
`;

const OffsetContainer = styled(Row)`
  overflow: hidden;
  height: 32px;
  left: 14px;
  width: fit-content;
`;

const FlyoutText = styled(Typography).attrs({
  variant: "paragraphSansMedium",
})`
  &&& {
    font-size: 12px;
    padding: 6px 10px 6px 0;
  }
`;

const InnerBorder = styled.div<{
  $backgroundColor?: string;
  $hasBorder?: boolean;
}>`
  ${({ $hasBorder }) =>
    $hasBorder
      ? css`
          box-shadow: inset 0 0 0 1px ${Core.surface.subdued};
        `
      : null}
  border-radius: 50px;
  position: absolute;
  background: ${({ $backgroundColor }) =>
    $backgroundColor}; // special color in gradient
  width: 32px;
  height: inherit;
  transition: width 0.5s ease-in-out;
`;

const BadgeSvg = styled(EventIcon)`
  height: 32px;
  width: 32px;
  padding: 7px;
  border-radius: 100px;
  transition: transform 0.5s cubic-bezier(0.82, 0.2, 0.41, 1.46);
  z-index: 1;
`;

const Wrapper = styled(Row).attrs({ align: "center" })<{
  $animated: boolean;
  $hoverBackgroundColor: string;
}>`
  position: relative;
  width: 32px;
  height: 32px;
  transition: width 0.5s ease-in-out;
  ${(props) =>
    props.$animated
      ? css`
          &:hover {
            width: 100%;
            & > ${OffsetContainer} > .flyout-text-container {
              opacity: 1;
              transform: translateX(0);
            }

            & > ${BadgeSvg} {
              transform: rotate(90deg);
            }

            & > .inner-border {
              background: ${props.$hoverBackgroundColor};
              width: 100%;
              transition: width 0.5s ease-in-out;
            }
          }
        `
      : ""}
`;

const FlyoutTextContainer = styled(Row)`
  user-select: none;
  white-space: nowrap;
  border-radius: 0 80px 80px 0;
  height: 32px;
  opacity: 0;
  transform: translateX(-100%);
  ${responsiveMediaQueries.canHover} {
    ${Wrapper}:hover & {
      opacity: 1;
      transform: translateX(0);
    }
  }
  transition:
    opacity 0.5s ease-in-out,
    transform 0.5s ease-in-out;
`;
